import React from 'react';
import { Box, Alert, AlertIcon, Button, VStack, Text } from '@chakra-ui/react';

interface ErrorBoundaryState {
  hasError: boolean;
  error?: Error;
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error?: Error; retry: () => void }>;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  retry = () => {
    this.setState({ hasError: false, error: undefined });
  };

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} retry={this.retry} />;
      }

      return (
        <Box p={4} maxW="md" mx="auto" mt={8}>
          <Alert status="error" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center">
            <AlertIcon boxSize="40px" mr={0} />
            <VStack spacing={3} mt={4}>
              <Text fontWeight="bold" fontSize="lg">
                Something went wrong
              </Text>
              <Text fontSize="sm" color="gray.600">
                {this.state.error?.message || 'An unexpected error occurred'}
              </Text>
              <Button size="sm" onClick={this.retry}>
                Try Again
              </Button>
            </VStack>
          </Alert>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary; 